// ttsService.ts — Client-side TTS audio playback
//
// Fetches streamed audio from our API route, queues playback,
// and provides interrupt capability. Anna always speaks through
// text in UI; TTS is an optional audio overlay.

import { getAuth } from 'firebase/auth';

export interface TTSOptions {
  text: string;
  voiceId?: string;
}

export interface TTSPlayback {
  done: Promise<void>;
  stop: () => void;
  isPlaying: () => boolean;
}

export async function speakText(options: TTSOptions): Promise<TTSPlayback> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const response = await fetch('/api/tts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ text: options.text, voiceId: options.voiceId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error || `TTS HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);

  let playing = true;

  const done = new Promise<void>((resolve) => {
    audio.onended = () => {
      playing = false;
      URL.revokeObjectURL(url);
      resolve();
    };
    audio.onerror = () => {
      playing = false;
      URL.revokeObjectURL(url);
      resolve();
    };
  });

  audio.play().catch(() => {
    // Autoplay may be blocked — resolve gracefully without throwing
    playing = false;
    URL.revokeObjectURL(url);
  });

  return {
    done,
    stop: () => {
      audio.pause();
      audio.currentTime = 0;
      playing = false;
      URL.revokeObjectURL(url);
    },
    isPlaying: () => playing,
  };
}
